import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { tasksService } from '../services/api';
import io from 'socket.io-client';
import { useToast } from './Toast';
import { LoadingButton } from './LoadingSpinner';
import DOMPurify from 'dompurify';
import CardBackgroundSelector from './CardBackgroundSelector';
import MediaPanelEditor from './MediaPanelEditor';
import CopyTaskModal from './CopyTaskModal';

// Helper: get user-friendly error message (defined but not currently used)
// eslint-disable-next-line no-unused-vars
function getErrorMessage(err, context = 'action') {
  if (!err.response) {
    if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
      return `Request timed out. Please check your connection and try again.`;
    }
    if (err.message?.includes('Network Error')) {
      return `Cannot connect to server. Please check your internet connection.`;
    }
    return `Network error. Please check your connection and try again.`;
  }

  const status = err.response?.status;
  const serverMessage = err.response?.data?.message;

  switch (status) {
    case 400:
      return serverMessage || `Invalid request. Please check your input and try again.`;
    case 401:
      return `Session expired. Please log in again.`;
    case 403:
      return `You don't have permission to ${context}. Contact the workspace owner.`;
    case 404:
      return serverMessage || `Item not found. It may have been deleted.`;
    case 409:
      return serverMessage || `Conflict detected. The item may have been modified by someone else.`;
    case 413:
      return `File too large. Maximum size is 10 MB.`;
    case 422:
      return serverMessage || `Invalid data. Please check your input.`;
    case 429:
      return `Too many requests. Please wait a moment and try again.`;
    case 500:
      return `Server error. Our team has been notified. Please try again later.`;
    case 502:
    case 503:
    case 504:
      return `Server temporarily unavailable. Please try again in a moment.`;
    default:
      if (serverMessage) return serverMessage;
      if (err.message && !err.message.includes('Request failed')) {
        return err.message;
      }
      return `Unable to ${context}. Please try again or contact support if this persists.`;
  }
}

// Unified status list
const DEFAULT_STATUSES = ['todo', 'doing', 'done'];

/**
 * Normalizes task status: maps 'in_progress' to 'doing'
 * and defaults unknown or empty statuses to 'todo'.
 */
function normaliseStatus(status) {
  const s = (status || '').toLowerCase();
  if (s === 'in_progress') return 'doing';
  if (DEFAULT_STATUSES.includes(s)) return s;
  return 'todo';
}

/**
 * Detects if text contains HTML tags
 * SECURITY: Used to determine if we should render as HTML or plain text
 */
function containsHTML(text) {
  if (!text || typeof text !== 'string') return false;
  // Check for HTML tags (opening tags like <div>, <p>, <h1>, etc.)
  return /<[a-z][\s\S]*>/i.test(text);
}

/**
 * Sanitize HTML with strict security configuration
 * SECURITY FIRST: Prevents XSS attacks by removing dangerous tags and attributes
 */
function sanitizeHTML(html) {
  if (!html) return '';

  return DOMPurify.sanitize(html, {
    // Whitelist only safe HTML tags
    ALLOWED_TAGS: [
      'div', 'p', 'span', 'br',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'strong', 'em', 'u', 'b', 'i',
      'a', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'blockquote', 'hr'
    ],
    // Whitelist only safe attributes
    ALLOWED_ATTR: [
      'style', 'class', 'id',
      'href', 'title', 'target',
      'colspan', 'rowspan'
    ],
    // Strict security settings
    ALLOW_DATA_ATTR: false,
    FORBID_TAGS: ['script', 'iframe', 'embed', 'object', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover', 'onfocus', 'onblur'],
    // Additional security
    KEEP_CONTENT: true,
    RETURN_TRUSTED_TYPE: false,
    SAFE_FOR_TEMPLATES: true
  });
}

export default function TaskModal({ task, collaborators, labels, listId, workspaces, onClose, onSave, onDelete, onRefreshLabels }) {
  const toast = useToast();
  const [title, setTitle] = useState(task?.title || '');
  const [status, setStatus] = useState(normaliseStatus(task?.status));
  const [dueDate, setDueDate] = useState(task?.due_date || '');
  const [assignedTo, setAssignedTo] = useState(task?.assigned_to_user_id || '');

  // Copy task modal state
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [color, setColor] = useState(task?.color || '');
  const [backgroundSettings, setBackgroundSettings] = useState(task?.background_settings || {});
  const [mediaPanel, setMediaPanel] = useState(task?.media_panel || null);
  const [bodyBefore, setBodyBefore] = useState('');
  const [bodyAfter, setBodyAfter] = useState('');
  const [checklist, setChecklist] = useState([]);
  const [wasChecklist, setWasChecklist] = useState(false);

  // Label management state
  const [taskLabels, setTaskLabels] = useState([]);
  const [showNewLabelForm, setShowNewLabelForm] = useState(false);
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState('#3b82f6');

  // Comment state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentContent, setEditingCommentContent] = useState('');
  const socketRef = useRef(null);

  // Activity log state
  const [activity, setActivity] = useState([]);

  // Attachments state
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);

  // Loading states for async operations
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // HTML preview mode state
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);

  // Sync state when task prop changes (e.g., after socket update)
  useEffect(() => {
    setTitle(task?.title || '');
    setStatus(normaliseStatus(task?.status));
    setDueDate(task?.due_date || '');
    setAssignedTo(task?.assigned_to_user_id || '');
    setColor(task?.color || '');
    setBackgroundSettings(task?.background_settings || {});
    setMediaPanel(task?.media_panel || null);
  }, [task?.id, task?.title, task?.status, task?.due_date, task?.assigned_to_user_id, task?.color, task?.background_settings, task?.media_panel]);

  useEffect(() => {
    const desc = String(task?.description || '');
    
    // The previous debug showed: "### Checklist\n\n- [ ] a\n- [ ] b\n- [ ] c"

    const lines = desc.split(/\r?\n/);
    // Find the Checklist header
    const headerIdx = lines.findIndex(l => /^#{1,6}\s*Checklist\b/i.test(l.trim()));
    const isChecklistTask = headerIdx !== -1;

    setWasChecklist(isChecklistTask);

    if (!isChecklistTask) {
      setBodyBefore(desc);
      setBodyAfter('');
      setChecklist([]);
      return;
    }

    const beforeLines = lines.slice(0, headerIdx);
    let afterHeader = lines.slice(headerIdx + 1);

    const items = [];
    let i = 0;
    
    // Pattern: optional whitespace, [- or *], optional space, [space or x/X], optional space, then text.
    const CHECKLIST_ITEM_REGEX = /^\s*[-*]\s*\[( |x|X)\]\s*(.*)$/i; 

    // AGGRESSIVE LOOP: Loop until the first non-checklist line that has content.
    for (; i < afterHeader.length; i++) {
      const line = afterHeader[i];
      const trimmedLine = line.trim();
      
      const m = line.match(CHECKLIST_ITEM_REGEX);
      
      if (m) {
          // This is a checklist item: add it and continue.
          items.push({
              checked: m[1].toLowerCase() === 'x',
              text: m[2] || ''
          });
      } else if (items.length > 0 && trimmedLine) {
          // If we have items AND this line has content AND it's not an item, then we break.
          break;
      }
      // If the line is empty, or if we haven't found items yet, we continue (skip the line).
    }

    // Capture all remaining lines for bodyAfter
    const afterLines = afterHeader.slice(i).join('\n').trim();

    setBodyBefore(beforeLines.join('\n').trim());
    setBodyAfter(afterLines);
    setChecklist(items);
  }, [task]);

  // Auto-enable HTML preview when HTML is detected in description
  useEffect(() => {
    const hasHTML = containsHTML(bodyBefore) || containsHTML(bodyAfter);
    setShowHtmlPreview(hasHTML);
  }, [bodyBefore, bodyAfter]);

  // Load task labels
  useEffect(() => {
    if (!task?.id) return;
    tasksService.getLabelsForTask(task.id)
      .then(res => setTaskLabels(res.data?.labels || []))
      .catch(err => console.error('[TaskModal] Failed to load labels:', err));
  }, [task?.id]);

  // Load comments and set up socket listeners
  useEffect(() => {
    if (!task?.id) return;

    // Load comments
    tasksService.getCommentsForTask(task.id)
      .then(res => {
        setComments(res.data?.comments || []);
        // Mark comments as read when modal opens
        tasksService.markCommentsAsRead(task.id)
          .catch(err => console.error('[TaskModal] Failed to mark comments as read:', err));
      })
      .catch(err => console.error('[TaskModal] Failed to load comments:', err));

    // Set up socket for real-time comment updates
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const socket = io(process.env.REACT_APP_WS_URL || 'http://localhost:3001', { auth: { token } });
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[TaskModal] Socket connected:', socket.id);
      // Join the list room
      if (listId) socket.emit('join_list', listId);
    });

    socket.on('comment_created', ({ comment }) => {
      console.log('[TaskModal] comment_created:', comment);
      if (comment?.task_id === task.id) {
        setComments(prev => [...prev, comment]);
      }
    });

    socket.on('comment_updated', ({ comment }) => {
      console.log('[TaskModal] comment_updated:', comment);
      if (comment?.task_id === task.id) {
        setComments(prev => prev.map(c => c.id === comment.id ? comment : c));
      }
    });

    socket.on('comment_deleted', ({ commentId, taskId }) => {
      console.log('[TaskModal] comment_deleted:', commentId, taskId);
      if (taskId === task.id) {
        setComments(prev => prev.filter(c => c.id !== commentId));
      }
    });

    return () => {
      if (listId) socket.emit('leave_list', listId);
      socket.disconnect();
      socketRef.current = null;
    };
  }, [task?.id, listId]);

  // Load activity log
  useEffect(() => {
    if (!task?.id) return;
    tasksService.getActivityForTask(task.id)
      .then(res => setActivity(res.data?.activity || []))
      .catch(err => console.error('[TaskModal] Failed to load activity:', err));
  }, [task?.id]);

  // Load attachments (wrapped in useCallback to avoid ESLint warning)
  const loadAttachments = useCallback(async () => {
    if (!task?.id) return;
    try {
      const res = await tasksService.getTaskAttachments(task.id);
      setAttachments(res.data?.attachments || []);
    } catch (err) {
      console.error('[TaskModal] Failed to load attachments:', err);
    }
  }, [task?.id]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  // Handle file upload
  async function handleFileUpload(event) {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (10 MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.warning('File size must be less than 10 MB');
      return;
    }

    setUploadingFile(true);
    try {
      await tasksService.uploadAttachment(task.id, file);
      await loadAttachments(); // Reload attachments
      // Reload activity log to show upload event
      const activityRes = await tasksService.getActivityForTask(task.id);
      setActivity(activityRes.data?.activity || []);
      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('[TaskModal] Failed to upload file:', err);
      const errorMsg = err.response?.data?.message || 'Failed to upload file';
      toast.error(errorMsg);
    } finally {
      setUploadingFile(false);
    }
  }

  // Handle file download
  async function handleDownloadAttachment(attachment) {
    try {
      const res = await tasksService.downloadAttachment(attachment.id);

      // Create blob and download
      const blob = new Blob([res.data], { type: attachment.mime_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[TaskModal] Failed to download file:', err);
      toast.error('Failed to download file');
    }
  }

  // Handle file delete
  async function handleDeleteAttachment(attachmentId) {
    if (!window.confirm('Are you sure you want to delete this attachment?')) {
      return;
    }

    try {
      await tasksService.deleteAttachment(attachmentId);
      await loadAttachments(); // Reload attachments
      // Reload activity log to show delete event
      const activityRes = await tasksService.getActivityForTask(task.id);
      setActivity(activityRes.data?.activity || []);
    } catch (err) {
      console.error('[TaskModal] Failed to delete attachment:', err);
      toast.error('Failed to delete attachment');
    }
  }

  // Format file size
  function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  const statusOptions = useMemo(() => {
    const currentStatus = normaliseStatus(status);
    return DEFAULT_STATUSES.includes(currentStatus)
      ? DEFAULT_STATUSES
      : [...DEFAULT_STATUSES, currentStatus];
  }, [status]);

  function composeDescription() {
    const parts = [];
    const before = (bodyBefore || '').trim();
    const after = (bodyAfter || '').trim();

    if (before) parts.push(before);

    // Always include header if it was a checklist task or items were added
    if (wasChecklist || checklist.length > 0) {
      parts.push('### Checklist');
      checklist.forEach(it => {
        parts.push(`- [${it.checked ? 'x' : ' '}] ${it.text}`);
      });
    }

    if (after) parts.push(after);

    return parts.join('\n\n');
  }

  // Label handlers
  async function handleCreateLabel() {
    if (!newLabelName.trim() || !listId) return;
    try {
      await tasksService.createLabel(listId, newLabelName.trim(), newLabelColor);
      setNewLabelName('');
      setNewLabelColor('#3b82f6');
      setShowNewLabelForm(false);
      await onRefreshLabels?.(listId);
    } catch (err) {
      toast.error('Failed to create label: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    }
  }

  async function handleAddLabel(labelId) {
    if (!task?.id) return;
    try {
      await tasksService.addLabelToTask(task.id, labelId);
      const label = labels.find(l => l.id === labelId);
      if (label) setTaskLabels(prev => [...prev, label]);
    } catch (err) {
      toast.error('Failed to add label: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    }
  }

  async function handleRemoveLabel(labelId) {
    if (!task?.id) return;
    try {
      await tasksService.removeLabelFromTask(task.id, labelId);
      setTaskLabels(prev => prev.filter(l => l.id !== labelId));
    } catch (err) {
      toast.error('Failed to remove label: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    }
  }

  // Comment handlers
  async function handleAddComment() {
    if (!newComment.trim() || !task?.id) return;
    try {
      await tasksService.createComment(task.id, newComment.trim());
      setNewComment('');
    } catch (err) {
      toast.error('Failed to add comment: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    }
  }

  async function handleEditComment(commentId) {
    if (!editingCommentContent.trim()) return;
    try {
      await tasksService.updateComment(commentId, editingCommentContent.trim());
      setEditingCommentId(null);
      setEditingCommentContent('');
    } catch (err) {
      toast.error('Failed to update comment: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    }
  }

  async function handleDeleteComment(commentId) {
    const ok = window.confirm('Delete this comment?');
    if (!ok) return;
    try {
      await tasksService.deleteComment(commentId);
    } catch (err) {
      toast.error('Failed to delete comment: ' + (err?.response?.data?.message || err?.message || 'Unknown error'));
    }
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }

  function formatActivity(log) {
    const user = log.full_name || log.username || log.email || 'Someone';
    const action = log.action;
    const details = log.details;

    switch (action) {
      case 'created':
        return `${user} created this task`;
      case 'updated':
        if (details?.changes) {
          const changeList = Object.keys(details.changes).map(field => {
            const change = details.changes[field];
            const fieldName = field.replace('_', ' ');
            if (field === 'status') {
              return `status from "${change.from}" to "${change.to}"`;
            }
            if (field === 'due_date') {
              return `due date from "${change.from || 'none'}" to "${change.to || 'none'}"`;
            }
            if (field === 'assigned_to') {
              return `assignee`;
            }
            return fieldName;
          });
          return `${user} updated ${changeList.join(', ')}`;
        }
        return `${user} updated this task`;
      case 'deleted':
        return `${user} deleted this task`;
      case 'file_uploaded':
        return `${user} uploaded file "${details?.filename || 'a file'}"`;
      case 'file_deleted':
        return `${user} deleted file "${details?.filename || 'a file'}"`;
      default:
        return `${user} ${action}`;
    }
  }

  async function handleSave() {
    const updates = {};
    if (title !== task.title) updates.title = title;

    const newDesc = composeDescription();
    const currentTaskDesc = (task.description || '');
    if (newDesc !== currentTaskDesc) updates.description = newDesc;

    const currentStatus = normaliseStatus(task.status);
    const newStatus = normaliseStatus(status);
    if (newStatus !== currentStatus) updates.status = newStatus;

    // Handle due date updates (including clearing)
    const currentDueDate = task.due_date || '';
    const newDueDate = dueDate || '';
    if (newDueDate !== currentDueDate) {
      updates.due_date = newDueDate || null; // null to clear
    }

    // Handle assigned_to updates
    const currentAssignedTo = task.assigned_to_user_id || '';
    const newAssignedTo = assignedTo || '';
    if (newAssignedTo !== currentAssignedTo) {
      updates.assigned_to_user_id = newAssignedTo || null;
    }

    // Handle color updates
    const currentColor = task.color || '';
    const newColor = color || '';
    if (newColor !== currentColor) {
      updates.color = newColor;
    }

    // Handle background_settings updates
    const currentBgSettings = JSON.stringify(task.background_settings || {});
    const newBgSettings = JSON.stringify(backgroundSettings || {});
    if (newBgSettings !== currentBgSettings) {
      updates.background_settings = backgroundSettings;
    }

    // Handle media_panel updates
    const currentMediaPanel = JSON.stringify(task.media_panel || null);
    const newMediaPanel = JSON.stringify(mediaPanel);
    if (currentMediaPanel !== newMediaPanel) {
      updates.media_panel = mediaPanel;
    }

    if (Object.keys(updates).length === 0) {
      onClose?.();
      return;
    }

    setSaving(true);
    try {
      await onSave?.(updates);
      onClose?.();
    } catch (err) {
      toast.error(
        'Failed to save task: ' +
          (err?.response?.data?.message || err?.message || 'Unknown error')
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    const ok = window.confirm('Delete this task? This cannot be undone.');
    if (!ok) return;
    setDeleting(true);
    try {
      await onDelete?.();
      onClose?.();
    } catch (err) {
      toast.error(
        'Failed to delete task: ' +
          (err?.response?.data?.message || err?.message || 'Unknown error')
      );
    } finally {
      setDeleting(false);
    }
  }

  async function handleCopyTask(targetListId, options) {
    try {
      await tasksService.copyTask(task.id, targetListId, options);
      toast.success('Task copied successfully!');
      // Optionally refresh the current list
      // onRefresh?.();
    } catch (err) {
      // Error will be shown in CopyTaskModal
      throw err;
    }
  }

  return (
    <div className="ModalOverlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className="TaskEditPanel" role="dialog" aria-modal="true" aria-label="Edit Task">
        {/* Header */}
        <div className="TaskEditHeader">
          <div className="TaskEditHeaderLeft">
            <div className="TaskEditHeading">Edit Task</div>
            <div className="TaskEditSub">
              <span className="TaskEditMono">{task?.id}</span>
            </div>
          </div>
          <div className="TaskEditHeaderRight">
            <button className="Btn" onClick={onClose}>Close</button>
          </div>
        </div>

        {/* Title */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Title</label>
          <input
            className="TaskEditTitleInput"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
          />
        </div>

        {/* Status */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Status</label>
          <select
            className="TaskStatusSelect"
            value={normaliseStatus(status)}
            onChange={(e) => setStatus(e.target.value)}
          >
            {statusOptions.map((s) => (
              <option key={s} value={s}>{normaliseStatus(s)}</option>
            ))}
          </select>
        </div>

        {/* Due Date */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Due Date</label>
          <input
            type="date"
            className="TaskEditInput"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            placeholder="YYYY-MM-DD"
          />
        </div>

        {/* Assigned To */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Assigned To</label>
          <select
            className="TaskStatusSelect"
            value={assignedTo}
            onChange={(e) => setAssignedTo(e.target.value)}
          >
            <option value="">Unassigned</option>
            {collaborators?.map((collab) => (
              <option key={collab.id} value={collab.id}>
                {collab.name || collab.email}
              </option>
            ))}
          </select>
        </div>

        {/* Card Colour */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Card Colour</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <input
              type="color"
              value={color || '#ffffff'}
              onChange={(e) => setColor(e.target.value)}
              style={{
                width: 60,
                height: 36,
                border: '1px solid var(--border-color)',
                borderRadius: 4,
                cursor: 'pointer',
                backgroundColor: color || 'transparent'
              }}
              title="Choose card colour"
            />
            <input
              type="text"
              className="TaskEditInput"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              placeholder="#3b82f6 or leave empty for default"
              pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
              style={{ flex: 1, maxWidth: 300 }}
            />
            {color && (
              <button
                className="Btn"
                onClick={() => setColor('')}
                style={{ fontSize: 12, padding: '6px 12px' }}
                title="Reset to default colour"
              >
                Reset
              </button>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4 }}>
            Choose a custom colour for this task card, or leave empty to use the default theme colour
          </div>
        </div>

        {/* Card Background */}
        <CardBackgroundSelector
          initialSettings={backgroundSettings}
          onChange={setBackgroundSettings}
        />

        {/* Media Panel Editor */}
        <MediaPanelEditor
          mediaPanel={mediaPanel}
          onChange={setMediaPanel}
        />

        {/* Labels */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Labels</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Current labels */}
            {taskLabels.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {taskLabels.map((label) => (
                  <span
                    key={label.id}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 4,
                      padding: '4px 8px',
                      borderRadius: 4,
                      backgroundColor: label.color,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 500,
                    }}
                  >
                    {label.name}
                    <button
                      onClick={() => handleRemoveLabel(label.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#fff',
                        cursor: 'pointer',
                        padding: 0,
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                      title="Remove label"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Available labels to add */}
            {labels && labels.length > 0 && (
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                  Add label:
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {labels
                    .filter(label => !taskLabels.some(tl => tl.id === label.id))
                    .map((label) => (
                      <button
                        key={label.id}
                        onClick={() => handleAddLabel(label.id)}
                        style={{
                          padding: '4px 8px',
                          borderRadius: 4,
                          backgroundColor: label.color,
                          color: '#fff',
                          fontSize: 12,
                          fontWeight: 500,
                          border: 'none',
                          cursor: 'pointer',
                        }}
                        title="Add this label"
                      >
                        + {label.name}
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Create new label */}
            {!showNewLabelForm ? (
              <button
                className="Btn"
                onClick={() => setShowNewLabelForm(true)}
                style={{ alignSelf: 'flex-start', fontSize: 12 }}
              >
                + Create new label
              </button>
            ) : (
              <div className="LabelForm">
                <input
                  type="text"
                  placeholder="Label name"
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateLabel();
                    }
                    if (e.key === 'Escape') {
                      setShowNewLabelForm(false);
                      setNewLabelName('');
                      setNewLabelColor('#3b82f6');
                    }
                  }}
                  className="LabelFormInput"
                  autoFocus
                />
                <div className="LabelFormActions">
                  <input
                    type="color"
                    value={newLabelColor}
                    onChange={(e) => setNewLabelColor(e.target.value)}
                    style={{ width: 40, height: 30, border: 'none', borderRadius: 4, cursor: 'pointer' }}
                  />
                  <button
                    className="Btn primary"
                    onClick={handleCreateLabel}
                    style={{ fontSize: 12 }}
                  >
                    Create
                  </button>
                  <button
                    className="Btn"
                    onClick={() => {
                      setShowNewLabelForm(false);
                      setNewLabelName('');
                      setNewLabelColor('#3b82f6');
                    }}
                    style={{ fontSize: 12 }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Description (before) */}
        <div className="TaskEditRow">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <label className="TaskEditLabel" style={{ margin: 0 }}>Description (above checklist)</label>
            {(containsHTML(bodyBefore) || containsHTML(bodyAfter)) && (
              <button
                className="Btn"
                onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                style={{ fontSize: 12, padding: '4px 12px' }}
              >
                {showHtmlPreview ? '✏️ Edit HTML' : '👁️ Preview HTML'}
              </button>
            )}
          </div>
          {showHtmlPreview && containsHTML(bodyBefore) ? (
            <div
              className="TaskEditTextarea"
              style={{
                padding: 16,
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: 6,
                minHeight: 100,
                maxHeight: 400,
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(bodyBefore) }}
            />
          ) : (
            <textarea
              className="TaskEditTextarea"
              value={bodyBefore}
              onChange={(e) => setBodyBefore(e.target.value)}
              placeholder="Notes, context, links… (optional)"
            />
          )}
        </div>

        {/* Checklist View (Non-Interactive) */}
        {wasChecklist && (
          <div className="TaskEditRow">
            <label className="TaskEditLabel">Interactive Checklist Items</label>
            {checklist.length > 0 ? (
              <ul className="ChecklistDisplay">
                {checklist.map((it, idx) => (
                  <li key={idx} className="ChecklistDisplayItem">
                    {/* Rendered as text, not a checkbox input, as edits are done on the board */}
                    <span className="ChecklistDisplayIcon">{it.checked ? '☑' : '☐'}</span>
                    <div className="ChecklistDisplayText">{it.text}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: 'var(--text-tertiary)', fontStyle: 'italic' }}>
                No items added. Add and edit items directly on the board card.
              </p>
            )}
          </div>
        )}

        {/* Description (after) */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Additional notes (below checklist)</label>
          {showHtmlPreview && containsHTML(bodyAfter) ? (
            <div
              className="TaskEditTextarea"
              style={{
                padding: 16,
                backgroundColor: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: 6,
                minHeight: 100,
                maxHeight: 400,
                overflow: 'auto'
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(bodyAfter) }}
            />
          ) : (
            <textarea
              className="TaskEditTextarea"
              value={bodyAfter}
              onChange={(e) => setBodyAfter(e.target.value)}
              placeholder="Anything else… (optional)"
            />
          )}
        </div>

        {/* Comments Section */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Comments ({comments.length})</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Add new comment */}
            <div className="CommentInputContainer">
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment..."
                className="CommentTextarea"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                    handleAddComment();
                  }
                }}
              />
              <button
                className="Btn primary CommentPostButton"
                onClick={handleAddComment}
                disabled={!newComment.trim()}
              >
                Post
              </button>
            </div>

            {/* Comment list */}
            {comments.length > 0 && (
              <div className="CommentsList">
                {comments.map((comment) => (
                  <div key={comment.id} className="CommentCard">
                    {/* Comment header */}
                    <div className="CommentHeader">
                      <div className="CommentMeta">
                        <span className="CommentAuthor">
                          {comment.full_name || comment.username || comment.email}
                        </span>
                        <span className="CommentDate">
                          {formatDate(comment.created_at)}
                          {comment.updated_at && ' (edited)'}
                        </span>
                      </div>

                      {/* Edit/Delete buttons (only for comment author) */}
                      <div className="CommentActions">
                        {editingCommentId === comment.id ? (
                          <>
                            <button
                              className="Btn primary CommentActionButton"
                              onClick={() => handleEditComment(comment.id)}
                            >
                              Save
                            </button>
                            <button
                              className="Btn CommentActionButton"
                              onClick={() => {
                                setEditingCommentId(null);
                                setEditingCommentContent('');
                              }}
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className="Btn CommentActionButton"
                              onClick={() => {
                                setEditingCommentId(comment.id);
                                setEditingCommentContent(comment.content);
                              }}
                              title="Edit comment"
                            >
                              Edit
                            </button>
                            <button
                              className="Btn danger CommentActionButton"
                              onClick={() => handleDeleteComment(comment.id)}
                              title="Delete comment"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Comment content */}
                    {editingCommentId === comment.id ? (
                      <textarea
                        value={editingCommentContent}
                        onChange={(e) => setEditingCommentContent(e.target.value)}
                        className="CommentTextarea"
                      />
                    ) : (
                      <div className="CommentContent">
                        {comment.content}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {comments.length === 0 && (
              <p className="CommentsEmpty">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </div>

        {/* Activity Log */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Activity Log</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {activity.length > 0 ? (
              <div className="ActivityLogContainer">
                {activity.map((log) => (
                  <div key={log.id} className="ActivityLogItem">
                    <div className="ActivityLogMessage">
                      {formatActivity(log)}
                    </div>
                    <div className="ActivityLogDate">
                      {formatDate(log.created_at)}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="ActivityLogEmpty">
                No activity yet
              </p>
            )}
          </div>
        </div>

        {/* Attachments */}
        <div className="TaskEditRow">
          <label className="TaskEditLabel">Attachments</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Upload button */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={uploadingFile}
              />
              <LoadingButton
                className="Btn"
                onClick={() => fileInputRef.current?.click()}
                loading={uploadingFile}
                style={{ fontSize: 13 }}
              >
                📎 Attach File
              </LoadingButton>
              <div className="AttachmentUploadInfo">
                Max 10 MB per file. Images, PDFs, docs, spreadsheets allowed.
              </div>
            </div>

            {/* Attachments list */}
            {attachments.length > 0 ? (
              <div className="AttachmentsList">
                {attachments.map((attachment) => (
                  <div key={attachment.id} className="AttachmentItem">
                    <div className="AttachmentInfo">
                      <div className="AttachmentFilename">
                        {attachment.filename}
                      </div>
                      <div className="AttachmentMeta">
                        {formatFileSize(attachment.file_size)} • Uploaded by {attachment.full_name || attachment.username || attachment.email}
                      </div>
                    </div>
                    <div className="AttachmentActions">
                      <button
                        className="Btn AttachmentActionButton"
                        onClick={() => handleDownloadAttachment(attachment)}
                        title="Download"
                      >
                        ⬇️
                      </button>
                      <button
                        className="Btn danger AttachmentActionButton"
                        onClick={() => handleDeleteAttachment(attachment.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="AttachmentsEmpty">
                No attachments yet
              </p>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="TaskEditActions">
          <LoadingButton className="Btn primary" onClick={handleSave} loading={saving}>
            Save
          </LoadingButton>

          <button
            className="Btn secondary"
            onClick={() => setShowCopyModal(true)}
            style={{ marginLeft: '8px' }}
          >
            Copy to...
          </button>

          <div className="TaskDangerZone">
            <LoadingButton className="Btn danger fullwidth" onClick={handleDelete} loading={deleting}>
              Delete Task
            </LoadingButton>
          </div>
        </div>
      </div>

      {/* Copy Task Modal */}
      {showCopyModal && workspaces && (
        <CopyTaskModal
          task={task}
          workspaces={workspaces}
          onCopy={handleCopyTask}
          onClose={() => setShowCopyModal(false)}
        />
      )}
    </div>
  );
}